<template>
  <div class="member-context-bar">
    <!-- PRE-SCAN STATE -->
    <div v-if="!hasMember && !posLoading">
      <b-row align-v="center">
        <b-col cols="12" md="5">
          <qr-scanner
            v-if="showScanner"
            @scan-success="onScanSuccess"
            @scan-failed="onScanFailed"
            @manual-entry-requested="focusManualEntry"
          />
        </b-col>
        <b-col cols="12" md="7">
          <div class="member-search-wrapper">
            <b-input-group>
              <b-form-input
                ref="memberNumberInput"
                v-model="memberQuery"
                placeholder="Enter name or member number"
                autofocus
                autocomplete="off"
                @input="onSearchInput"
                @keyup.enter="onManualLookup"
                @focus="showSearchResults = true"
                @blur="onSearchBlur"
              />
              <b-input-group-append>
                <b-button variant="primary" @click="onManualLookup" :disabled="!memberQuery">
                  Look up
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <!-- Search results dropdown -->
            <div
              v-if="showSearchResults && (memberSearchResults.length > 0 || memberSearchLoading)"
              class="member-search-results"
            >
              <div v-if="memberSearchLoading" class="text-center py-2">
                <b-spinner small variant="primary" />
              </div>
              <div
                v-for="result in memberSearchResults"
                :key="result.id"
                class="member-search-result"
                @mousedown.prevent="onSelectSearchResult(result)"
              >
                <div class="result-name">{{ result.member_name }}</div>
                <div class="result-meta text-muted">
                  <span class="font-monospace">{{ result.member_number }}</span>
                  <span v-if="result.contact_no" class="ml-2">{{ result.contact_no }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="posError" class="text-danger mt-2">
            {{ posError.message }}
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- LOADING STATE -->
    <div v-if="posLoading" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <!-- MEMBER FOUND STATE -->
    <div v-if="hasMember && !posLoading">
      <b-row align-v="center">
        <b-col cols="auto">
          <b-avatar
            :src="currentMember.photo_url"
            :text="memberInitials"
            size="48px"
            variant="primary"
          />
        </b-col>
        <b-col>
          <h5 class="mb-0 member-name">{{ currentMember.member_name }}</h5>
          <span class="text-muted font-monospace">{{ currentMember.member_number }}</span>
          <b-badge variant="info" class="ml-2">{{ currentMember.pricing_tier }}</b-badge>
          <b-badge
            :variant="currentMember.membership_active ? 'success' : 'danger'"
            class="ml-1"
          >
            {{ currentMember.membership_active ? 'Active' : 'Inactive' }}
          </b-badge>
        </b-col>
        <b-col cols="auto">
          <b-button variant="outline-secondary" size="sm" @click="onClear">
            Clear
          </b-button>
        </b-col>
      </b-row>
      <!-- Inactive warning -->
      <b-alert
        v-if="!currentMember.membership_active"
        show
        variant="warning"
        class="mt-2 mb-0"
      >
        Membership inactive — ordering is not available for this member.
      </b-alert>
      <!-- Collapsible recent orders -->
      <div v-if="recentOrders.length > 0" class="mt-2">
        <b-link v-b-toggle.recent-orders class="text-muted small">
          Recent orders ({{ recentOrders.length }})
        </b-link>
        <b-collapse id="recent-orders">
          <div v-for="order in recentOrders" :key="order.id" class="recent-order-item mt-1 p-2">
            <small class="text-muted">{{ order.order_no }}</small>
            <span class="ml-2">R{{ order.order_total }}</span>
            <b-badge :variant="order.status === 'Completed' ? 'success' : 'secondary'" class="ml-2" size="sm">
              {{ order.status }}
            </b-badge>
          </div>
        </b-collapse>
      </div>
      <!-- Pending Orders Section — only shown when pending orders exist -->
      <div v-if="pendingOrderCount > 0" class="pending-orders-section mt-2">
        <div
          class="pending-orders-toggle d-flex align-items-center"
          role="button"
          @click="showPendingOrders = !showPendingOrders"
          :aria-expanded="showPendingOrders ? 'true' : 'false'"
        >
          <i class="fas fa-clock mr-2"></i>
          <span>Pending Orders</span>
          <b-badge variant="warning" pill class="ml-2">{{ pendingOrderCount }}</b-badge>
          <i
            class="fas ml-auto"
            :class="showPendingOrders ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </div>
        <b-collapse v-model="showPendingOrders" class="mt-2">
          <pending-order-card
            v-for="order in pendingOrders"
            :key="order.id"
            :order="order"
            :handovering="handoveringOrderId === order.id"
            @handover="onHandover"
          />
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import QrScanner from '@/components/pos/qrScanner.vue';
import PendingOrderCard from '@/components/pos/pendingOrderCard.vue';
import { posComputed, posMethods } from '@/state/helpers';

export default {
    name: 'MemberContextBar',
    components: {
        QrScanner,
        PendingOrderCard,
    },
    data() {
        return {
            memberQuery: '',
            showScanner: true,
            showPendingOrders: false,
            showSearchResults: false,
            searchDebounceTimer: null,
        };
    },
    computed: {
        ...posComputed,
        memberInitials() {
            if (!this.currentMember || !this.currentMember.member_name) return '';
            return this.currentMember.member_name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
    },
    methods: {
        ...posMethods,
        onScanSuccess(data) {
            this.lookupMember({ encrypted_id: data });
        },
        onScanFailed() {
            this.$nextTick(() => {
                if (this.$refs.memberNumberInput) {
                    this.$refs.memberNumberInput.focus();
                }
            });
        },
        focusManualEntry() {
            this.showScanner = false;
            this.$nextTick(() => {
                if (this.$refs.memberNumberInput) {
                    this.$refs.memberNumberInput.focus();
                }
            });
        },
        onManualLookup() {
            if (!this.memberQuery) return;
            this.showSearchResults = false;
            this.lookupMember({ member_number: this.memberQuery }).catch(() => {
                this.memberQuery = '';
            });
        },
        onSearchInput() {
            this.showSearchResults = true;
            if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
            const query = this.memberQuery;
            if (!query || query.length < 2) {
                this.clearMemberSearch();
                return;
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.searchMembers(query).catch(() => {});
            }, 250);
        },
        onSearchBlur() {
            // Delay so click on a result still registers
            setTimeout(() => {
                this.showSearchResults = false;
            }, 150);
        },
        onSelectSearchResult(result) {
            this.memberQuery = result.member_number;
            this.showSearchResults = false;
            this.clearMemberSearch();
            this.lookupMember({ member_number: result.member_number }).catch(() => {});
        },
        onClear() {
            if (this.orderItems.length > 0) {
                if (!window.confirm('Discard current order?')) return;
            }
            this.resetTransaction();
            this.memberQuery = '';
            this.showScanner = true;
            this.clearMemberSearch();
        },
        async onHandover(orderId) {
            try {
                await this.processHandover(orderId);
                this.$bvToast.toast('Order handed over', {
                    title: 'Handover Complete',
                    variant: 'success',
                    autoHideDelay: 3000,
                    noCloseButton: true,
                    toaster: 'b-toaster-bottom-right',
                });
            } catch (error) {
                this.$bvToast.toast(
                    error.response?.data?.error || 'Handover failed. Please try again.',
                    {
                        title: 'Handover Failed',
                        variant: 'danger',
                        autoHideDelay: 5000,
                        toaster: 'b-toaster-bottom-right',
                    }
                );
            }
        },
    },
};
</script>

<style scoped lang="scss">
.member-context-bar {
    padding: $space-3;
    background: $surface-card;
    border-radius: 8px;
}

.member-name {
    font-size: $type-h2;
    color: $text-primary;
}

.font-monospace {
    font-family: $font-family-mono;
}

.recent-order-item {
    background: $surface-elevated;
    border-radius: 4px;
}

.pending-orders-toggle {
    cursor: pointer;
    padding: 4px 0;
    font-size: $type-small;
    color: $text-muted;

    &:hover {
        color: $text-primary;
    }
}

.member-search-wrapper {
    position: relative;
}

.member-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1050;
    margin-top: 2px;
    background: $surface-card;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    max-height: 320px;
    overflow-y: auto;
}

.member-search-result {
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: $surface-elevated;
    }
}

.result-name {
    font-weight: 600;
    font-size: 0.9rem;
}

.result-meta {
    font-size: 0.8rem;
}
</style>
